import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl, TRAINER_COLOR_PRESETS } from "../../../utils/clientHeroTheme";
import ClinicLayout from "../ClinicLayout";
import s from "../clinic.module.css";
import { Avatar, Button, useToast } from "../../ui";

export default function TrainerBrandingSettings({ embedded = false }) {
  const toast = useToast();
  const [branding, setBranding] = useState(null);
  const [color, setColor] = useState("#6d28d9");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await clinicAPI.getMyTrainerBranding();
      const b = data.branding;
      setBranding(b);
      setColor(b?.portal_accent_color || "#6d28d9");
    } catch {
      toast.error("Не удалось загрузить настройки");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const saveColor = async (nextColor) => {
    setSaving(true);
    try {
      const data = await clinicAPI.updateMyTrainerBranding({
        portal_accent_color: nextColor,
      });
      setBranding(data.branding);
      setColor(data.branding?.portal_accent_color || nextColor);
      toast.success("Цвет сохранён");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const data = await clinicAPI.uploadMyTrainerPhoto(file);
      setBranding(data.branding);
      toast.success("Фото профиля обновлено");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const data = await clinicAPI.uploadMyTrainerCover(file);
      setBranding(data.branding);
      toast.success("Обложка обновлена");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  const removeCover = async () => {
    setSaving(true);
    try {
      const data = await clinicAPI.updateMyTrainerBranding({ portal_cover_url: "" });
      setBranding(data.branding);
      toast.success("Обложка убрана");
    } catch {
      toast.error("Не удалось убрать обложку");
    } finally {
      setSaving(false);
    }
  };

  const coverPreview = mediaUrl(branding?.portal_cover_url);
  const photoPreview = mediaUrl(branding?.photo_url);

  const inner = loading ? (
    <p className={s.trainerBrandHint}>Загрузка…</p>
  ) : (
    <div className={s.trainerBrandPage}>
          <section className={s.trainerBrandSection}>
            <h3 className={s.docSectionTitle}>Фото профиля</h3>
            <p className={s.trainerBrandHint}>
              Аватар отображается клиентам на главном экране приложения.
            </p>
            <div className={s.trainerBrandPhotoRow}>
              <Avatar name={branding?.full_name} src={photoPreview} size="lg" />
              <label className={s.profilePhotoBtn}>
                {photoUploading ? "Загрузка…" : "Загрузить фото"}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onPhoto} />
              </label>
            </div>
          </section>

          <section className={s.trainerBrandSection}>
            <h3 className={s.docSectionTitle}>Обложка главной</h3>
            <p className={s.trainerBrandHint}>
              Фото вместо цветного фона сверху в приложении клиента. Если обложки нет — используется выбранный цвет.
            </p>
            <div
              className={s.trainerBrandCoverPreview}
              style={
                coverPreview
                  ? { backgroundImage: `url(${coverPreview})` }
                  : { background: `linear-gradient(135deg, ${color}, ${color}cc)` }
              }
            >
              {!coverPreview ? (
                <span className={s.trainerBrandCoverPlaceholder}>Предпросмотр цвета</span>
              ) : null}
            </div>
            <div className={s.trainerBrandCoverActions}>
              <label className={s.profilePhotoBtn}>
                {coverUploading ? "Загрузка…" : "Загрузить обложку"}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onCover} />
              </label>
              {coverPreview ? (
                <Button type="button" variant="ghost" onClick={removeCover} disabled={saving}>
                  Убрать обложку
                </Button>
              ) : null}
            </div>
          </section>

          <section className={s.trainerBrandSection}>
            <h3 className={s.docSectionTitle}>Цвет приложения</h3>
            <p className={s.trainerBrandHint}>
              Акцентный цвет для шапки, кнопок и фона, если обложка не задана.
            </p>
            <div className={s.trainerBrandColors}>
              {TRAINER_COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className={`${s.trainerBrandColorBtn} ${
                    color === preset.color ? s.trainerBrandColorBtnOn : ""
                  }`}
                  style={{ "--brand-color": preset.color }}
                  onClick={() => {
                    setColor(preset.color);
                    saveColor(preset.color);
                  }}
                  disabled={saving}
                >
                  <span className={s.trainerBrandColorDot} aria-hidden />
                  {preset.label}
                </button>
              ))}
            </div>
            <div className={s.trainerBrandCustomColor}>
              <label htmlFor="trainer-custom-color">Свой цвет</label>
              <input
                id="trainer-custom-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                onBlur={() => saveColor(color)}
              />
              <code>{color}</code>
            </div>
          </section>
        </div>
  );

  if (embedded) return inner;

  return (
    <ClinicLayout
      title="Оформление для клиентов"
      subtitle="Цвет, обложка и фото — так клиенты видят ваш клуб в приложении"
    >
      {inner}
    </ClinicLayout>
  );
}
